@if (isLoading()) {
  <div class="pd-wrap">
    <div class="pd-layout">
      <div class="pd-gallery">
        <div class="pd-thumbs-v">
          @for (n of [1, 2, 3, 4, 5]; track n) {
            <div class="pd-skel pd-skel-thumb"></div>
          }
        </div>
        <div class="pd-skel pd-skel-main"></div>
      </div>
      <div class="pd-details">
        <div class="pd-skel pd-skel-line w40"></div>
        <div class="pd-skel pd-skel-line w80 tall"></div>
        <div class="pd-skel pd-skel-line w30"></div>
        <div class="pd-skel pd-skel-block"></div>
      </div>
      <div class="pd-buybox">
        <div class="pd-skel pd-skel-line w60 tall"></div>
        <div class="pd-skel pd-skel-cta"></div>
      </div>
    </div>
  </div>
} @else if (notFound()) {
  <div class="pd-empty">
    <h1>This product is no longer available</h1>
    <p>It may have sold out or been removed by the store. Browse the latest deals instead.</p>
    <a routerLink="/collection" class="pd-btn-outline">Back to Deals</a>
  </div>
} @else if (page(); as product) {
  <div class="pd-wrap">
    <app-breadcrumb [items]="crumbs()" />

    <div class="pd-layout">
      <!-- ===== Gallery (vertical thumbs + main) ===== -->
      <div class="pd-gallery">
        <!-- One image is not a gallery — the thumbnail strip only earns its space with alternates. -->
        @if (product.gallery.length > 1) {
          <div class="pd-thumbs-v" aria-label="Product gallery">
            <div class="pd-thumbs-scroll">
              @for (image of product.gallery; track image) {
                <button
                  type="button"
                  [class.active]="image === activeImage()"
                  (mouseenter)="setImage(image)"
                  (click)="setImage(image)"
                >
                  <img [src]="image" [alt]="product.name" loading="lazy" />
                </button>
              }
            </div>
          </div>
        }
        <div
          class="pd-main-img"
          [class.zooming]="zoomActive()"
          (mouseenter)="zoomActive.set(true)"
          (mouseleave)="zoomActive.set(false)"
          (mousemove)="onZoomMove($event)"
        >
          @if (product.discountBadge) {
            <span class="pd-media-badge">{{ product.discountBadge }}</span>
          }
          @if (activeImage()) {
            <img
              [src]="activeImage()"
              [alt]="product.name"
              [style.transform-origin]="zoomOrigin()"
            />
          } @else {
            <div class="pd-noimg" aria-hidden="true">No image</div>
          }
        </div>
      </div>

      <!-- ===== Details (middle) ===== -->
      <div class="pd-details">
        @if (product.storeName) {
          @if (product.storePath) {
            <a [routerLink]="product.storePath" class="pd-store">
              <!-- The name follows as text, so the mark is decorative; a store without a logo
                   shows its name on white rather than nothing. -->
              <app-store-logo
                class="pd-store-logo"
                [logoUrl]="product.storeLogoUrl"
                [name]="product.storeName"
                decorative
              />
              <span>Visit {{ product.storeName }}</span>
            </a>
          } @else {
            <span class="pd-store">
              <!-- The name follows as text, so the mark is decorative; a store without a logo
                   shows its name on white rather than nothing. -->
              <app-store-logo
                class="pd-store-logo"
                [logoUrl]="product.storeLogoUrl"
                [name]="product.storeName"
                decorative
              />
              <span>{{ product.storeName }}</span>
            </span>
          }
        }
        <h1>{{ product.name }}</h1>

        <hr class="pd-rule" />

        @if (product.description) {
          <p class="pd-short">{{ product.description }}</p>
        }
      </div>

      <!-- ===== Buy box (right, sticky) ===== -->
      <aside class="pd-buybox">
        <div class="pd-bb-price">
          <span class="now">{{ product.price }}</span>
          @if (product.originalPrice) {
            <span class="was">{{ product.originalPrice }}</span>
          }
          @if (product.savingsBadge) {
            <span class="pd-save">{{ product.savingsBadge }}</span>
          }
        </div>
        <span class="pd-bb-note">Price and availability are set by the store and can change.</span>

        @if (product.stock === 'in') {
          <div class="pd-stock in"><span class="dot"></span>In stock</div>
        } @else if (product.stock === 'out') {
          <div class="pd-stock out"><span class="dot"></span>Out of stock</div>
        }

        @if (product.affiliateLink) {
          <a
            class="pd-add"
            [href]="product.affiliateLink"
            target="_blank"
            rel="nofollow sponsored noopener"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              aria-hidden="true"
            >
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <path d="M15 3h6v6" />
              <path d="M10 14 21 3" />
            </svg>
            Get Deal
          </a>
        }

        <div class="pd-bb-delivery">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.6"
            aria-hidden="true"
          >
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <path d="M15 3h6v6" />
            <path d="M10 14 21 3" />
          </svg>
          <div>
            <strong>Continues at {{ product.storeName || 'the store' }}</strong>
            <span>You complete the purchase on the retailer's own site.</span>
          </div>
        </div>

        <div class="pd-bb-seller">
          @if (product.storeName) {
            <div>
              <span>Sold by</span>
              <strong class="pd-bb-seller-name">
                <app-store-logo
                  class="pd-store-logo sm"
                  [logoUrl]="product.storeLogoUrl"
                  [name]="product.storeName"
                  decorative
                />
                {{ product.storeName }}
              </strong>
            </div>
          }
          <div>
            <span>Category</span><strong>{{ product.categoryName }}</strong>
          </div>
        </div>
      </aside>
    </div>

    <!-- ===== Specifications (only rows the feed supplied) ===== -->
    @if (product.specRows.length > 0) {
      <section class="pd-specs-box">
        <h2 class="pd-specs-title type-widget-title">Product specifications</h2>
        <table class="pd-specs-table">
          <tbody>
            @for (row of product.specRows; track row.label) {
              <tr>
                <th scope="row">{{ row.label }}</th>
                <td>{{ row.value }}</td>
              </tr>
            }
          </tbody>
        </table>
      </section>
    }

    @if (related().length > 0) {
      <section class="pd-related">
        <h2>More from {{ product.storeName || 'this store' }}</h2>
        <!-- `sav-full-rows` trims a part-filled last row. Six related products fill the six, three
             and two column tiers exactly, but leave two stranded at four — see the note on that
             class in styles.scss. -->
        <div class="sav-product-grid sav-full-rows">
          @for (item of related(); track item.id) {
            <app-product-card
              [title]="item.name"
              [image]="item.imageUrl"
              [hoverImage]="item.hoverImageUrl"
              [price]="item.price"
              [oldPrice]="item.originalPrice"
              [href]="item.path"
            />
          }
        </div>
      </section>
    }
  </div>
}
